import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../../../config';
import { useSocket } from '../../../contexts/SocketContext';

export type UrlCheckStatus = 'idle' | 'checking' | 'available' | 'unavailable';

interface UrlCheckProgress {
  checked: number;
  total: number;
}

interface UseUrlCheckSocketResult {
  checking: boolean;
  checkProgress: UrlCheckProgress | null;
  checkResult: 'available' | 'unavailable' | null;
  triggerCheck: () => void;
  restoreStatus: () => void;
}

export const useUrlCheckSocket = (schemaId: string | undefined): UseUrlCheckSocketResult => {
  const [checking, setChecking] = useState(false);
  const [checkProgress, setCheckProgress] = useState<UrlCheckProgress | null>(null);
  const [checkResult, setCheckResult] = useState<'available' | 'unavailable' | null>(null);
  const { addEventListener } = useSocket();

  const schemaIdRef = useRef(schemaId);
  schemaIdRef.current = schemaId;

  useEffect(() => {
    const cleanups = [
      addEventListener('url-check-progress', (data: { schemaId: string; checked: number; total: number }) => {
        if (data.schemaId === schemaIdRef.current) {
          setCheckProgress({ checked: data.checked, total: data.total });
        }
      }),
      addEventListener('url-check-complete', (data: { schemaId: string; allAvailable: boolean }) => {
        if (data.schemaId === schemaIdRef.current) {
          setChecking(false);
          setCheckProgress(null);
          setCheckResult(data.allAvailable ? 'available' : 'unavailable');
        }
      }),
    ];

    return () => {
      for (const cleanup of cleanups) cleanup();
    };
  }, [addEventListener]);

  const triggerCheck = useCallback(async () => {
    if (!schemaIdRef.current) return;
    setChecking(true);
    setCheckProgress(null);
    setCheckResult(null);

    try {
      const res = await fetch(`${API_BASE_URL}/api/postman/check-urls/${schemaIdRef.current}`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        // Backend returns checking: false when there are no URLs to check
        if (!data.checking) {
          setChecking(false);
          setCheckResult('available');
        }
      } else {
        setChecking(false);
      }
    } catch {
      setChecking(false);
    }
  }, []);

  const restoreStatus = useCallback(async () => {
    if (!schemaIdRef.current) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/postman/url-status/${schemaIdRef.current}`);
      if (res.ok) {
        const data = await res.json();
        if (data.status === 'checking') {
          setChecking(true);
        } else if (data.status === 'available' || data.status === 'unavailable') {
          setCheckResult(data.status);
        }
      }
    } catch {
      // ignore
    }
  }, []);

  return { checking, checkProgress, checkResult, triggerCheck, restoreStatus };
};
